import { useEffect, useState } from 'react';

import { dequal } from 'dequal';

import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
// Hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
// Types
import type {
    RequestParamsType,
    SetRequestParamsType
} from '@/utils/hooks/swr/reports/useFetchReports';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Components
import Select, { OptionType } from '@/components/base/Select';

const SelectSpGroupsFilter = ({
    requestParams,
    setRequestParams
}: {
    requestParams: RequestParamsType;
    setRequestParams: SetRequestParamsType;
}) => {
    const { t } = useTranslation();
    const defaultOptions: OptionType[] = [
        { value: undefined, label: t('reports:general.all_groups') }
    ];

    const [options, setOptions] = useState(defaultOptions);
    const { spGroups } = useSPGroups({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [reportsSpGroups, setReportsSpGroups] = useState<SpGroupType['id'][]>([]);

    useEffect(() => {
        if (spGroups?.length) {
            setOptions((prevState) => [
                ...prevState,
                ...spGroups.map((group) => ({
                    value: group?.id.toString(),
                    label: group?.name
                }))
            ]);
            return setReportsSpGroups(spGroups?.map((el) => el?.id));
        }
        //Only reset options if spGroups is empty and options are not equal to defaultOptions
        if (!spGroups?.length && !dequal(options, defaultOptions)) {
            setOptions(defaultOptions);
        }
    }, [JSON.stringify(spGroups)]);

    useEffect(() => {
        if (spGroups?.length) {
            if (
                requestParams?.filter_group &&
                !reportsSpGroups?.includes(requestParams?.filter_group)
            ) {
                setRequestParams((prevState) => ({
                    ...prevState,
                    page: 1,
                    filter_group: undefined
                }));
            }
        }
    }, [JSON.stringify(spGroups)]);

    const selectedOptionValue = reportsSpGroups?.includes(requestParams?.filter_group)
        ? requestParams?.filter_group.toString()
        : undefined;

    return (
        <Select
            buttonBaseStyle="noBorder"
            className="w-full @2xl:w-44"
            isTruncate={false}
            setSelected={(option: OptionType) =>
                setRequestParams((prevState) => ({
                    ...prevState,
                    page: 1,
                    filter_group: option?.value ? parseInt(option?.value as string) : undefined
                }))
            }
            selected={options?.filter((item) => item?.value === selectedOptionValue)?.[0]}
            options={options}
            forcedDisplay
            data-testid="select-sp-groups-filter"
        />
    );
};

export default SelectSpGroupsFilter;
